import { Box, Flex, HStack, Text, Button, IconButton, useDisclosure } from "@chakra-ui/react";
import { Tooltip } from '@/components/ui/tooltip';
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { FiLogOut, FiChevronsLeft, FiChevronsRight, FiSettings } from "react-icons/fi";
import {useState, useEffect, useCallback} from "react";
import {useAuth} from "../../hooks/useAuth";
import {useProfile} from "../../hooks/useProfile";
import {signOutOrganizer} from "../../lib/session/organizerSession";
import {UserAvatar} from "../admin/UserAvatar";
import {ProfileEditModal} from "../profile/ProfileEditModal";
import {CommitteeSwitcher} from "./CommitteeSwitcher";
import { ModuleNav } from "./ModuleNav";
import { useTier } from "../../hooks/useTier";
import { MobileDashboardShell } from "./mobile/MobileDashboardShell";
import { TabletDashboardShell } from "./tablet/TabletDashboardShell";
import { MainContentRegion } from "./MainContentRegion";

export interface SidebarContextType {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

export function useSidebarContext() {
  return useOutletContext<SidebarContextType>();
}

const SIDEBAR_EXPANDED = "240px";
const SIDEBAR_COLLAPSED = "60px";

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const {isAdmin} = useAuth();
  const {data: profile} = useProfile();
  const profileModal = useDisclosure();

  const [collapsed, setCollapsedState] = useState(
    () => localStorage.getItem("sidebar_collapsed") === "true"
  );

  useEffect(() => {
    localStorage.setItem("sidebar_collapsed", String(collapsed));
  }, [collapsed]);

  const setCollapsed = useCallback((value: boolean) => {
    setCollapsedState(value);
  }, []);

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  const handleLogout = () => void signOutOrganizer();

  const tier = useTier();
  if (tier === "phone") {
    return <MobileDashboardShell />;
  }
  if (tier === "tablet") {
    return <TabletDashboardShell />;
  }
  // desktop falls through to the existing sidebar layout

  return (
    <Flex minH="100vh">
      {/* Sidebar */}
      <Box
        data-testid="navbar"
        className="no-print"
        w={sidebarWidth}
        bg="surface"
        borderRight="1px"
        borderColor="border"
        py={6}
        px={collapsed ? 1 : 4}
        position="fixed"
        h="100vh"
        display="flex"
        flexDirection="column"
        transition="width 0.2s, padding 0.2s"
        overflow="hidden"
        overflowX="hidden"
      >
        {/* Logo/Title */}
        <HStack
          flexShrink={0}
          gap={collapsed ? 0 : 2}
          px={collapsed ? 0 : 4}
          mb={8}
          justifyContent={collapsed ? "center" : "flex-start"}
        >
          <Box
            w={8}
            h={8}
            bg="accent"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Text color="accent.fg" fontWeight="bold" fontSize="lg">
              BT
            </Text>
          </Box>
          {!collapsed && (
            <Text fontWeight="bold" fontSize="lg">
              ORCL
            </Text>
          )}
        </HStack>

        {/* WL-6: the "Viewing:" committee switcher lives in the nav chrome under the ORCL banner
            (self-hides when the caller has no committees to switch to). Hidden in the collapsed
            rail — the select needs width the 60px rail can't give. */}
        {!collapsed && (
          <Box className="no-print" flexShrink={0} px={4} mb={6}>
            <CommitteeSwitcher />
          </Box>
        )}

        {/* Navigation */}
        <Box flex={1} minH={0} overflowY="auto" overflowX="hidden">
          <ModuleNav
            pathname={location.pathname}
            collapsed={collapsed}
            isAdmin={isAdmin}
          />
        </Box>

        {/* Footer: profile + collapse toggle + logout */}
        <Box flexShrink={0}>
          {/* Profile */}
          <Box pt={3} borderTop="1px" borderColor="border">
            {collapsed ? (
              <Tooltip content={`${profile?.firstName} ${profile?.lastName}`} showArrow positioning={{
                placement: "right"
              }}>
                <Box
                  cursor="pointer"
                  onClick={profileModal.onOpen}
                  _hover={{bg: "canvas.2"}}
                  borderRadius="md"
                  p={2}
                  display="flex"
                  justifyContent="center"
                >
                  <UserAvatar
                    userId={profile?.id ?? ""}
                    firstName={profile?.firstName ?? ""}
                    portraitUrl={profile?.portraitUrl}
                    size="sm"
                  />
                </Box>
              </Tooltip>
            ) : (
              <HStack
                gap={3}
                cursor="pointer"
                onClick={profileModal.onOpen}
                _hover={{bg: "canvas.2"}}
                borderRadius="md"
                p={2}
              >
                <UserAvatar
                  userId={profile?.id ?? ""}
                  firstName={profile?.firstName ?? ""}
                  portraitUrl={profile?.portraitUrl}
                  size="sm"
                />
                <Box flex={1} overflow="hidden">
                  <Text fontSize="sm" fontWeight="medium" truncate>
                    {profile?.firstName} {profile?.lastName}
                  </Text>
                  <Text fontSize="xs" color="muted" truncate>
                    {profile?.email}
                  </Text>
                </Box>
              </HStack>
            )}
          </Box>

          {/* Collapse toggle */}
          <Tooltip content={collapsed ? "Expand sidebar" : "Collapse sidebar"} showArrow positioning={{
            placement: "right"
          }}>
            <IconButton
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              variant="ghost"
              colorPalette="gray"
              size="sm"
              mt={2}
              onClick={() => setCollapsed(!collapsed)}
              w="100%">{collapsed ? <FiChevronsRight /> : <FiChevronsLeft />}</IconButton>
          </Tooltip>

          {/* Settings */}
          {collapsed ? (
            <Tooltip content="Settings" showArrow positioning={{
              placement: "right"
            }}>
              <IconButton
                aria-label="Settings"
                variant="ghost"
                colorPalette="gray"
                size="sm"
                mt={1}
                onClick={() => navigate('/dashboard/settings')}
                w="100%"><FiSettings /></IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="ghost"
              colorPalette="gray"
              justifyContent="flex-start"
              size="sm"
              mt={1}
              onClick={() => navigate('/dashboard/settings')}><FiSettings />Settings
            </Button>
          )}

          {/* Logout */}
          {collapsed ? (
            <Tooltip content="Logout" showArrow positioning={{
              placement: "right"
            }}>
              <IconButton
                aria-label="Logout"
                variant="ghost"
                colorPalette="gray"
                size="sm"
                mt={1}
                onClick={handleLogout}
                w="100%"><FiLogOut /></IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="ghost"
              colorPalette="gray"
              justifyContent="flex-start"
              size="sm"
              mt={1}
              onClick={handleLogout}><FiLogOut />Logout
                        </Button>
          )}
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        flex={1}
        ml={sidebarWidth}
        bg="canvas"
        minH="100vh"
        transition="margin-left 0.2s"
      >
        <MainContentRegion pathname={location.pathname}>
          <Outlet context={{collapsed, setCollapsed} satisfies SidebarContextType} />
        </MainContentRegion>
      </Box>

      {/* Profile Edit Modal */}
      <ProfileEditModal
        isOpen={profileModal.open}
        onClose={profileModal.onClose}
      />
    </Flex>
  );
}
